import { isCancel } from 'axios';
import type { QueryClient } from '@tanstack/react-query';
import type { User } from '@valiosascrm/shared';
import { waitForSessionRefresh } from '@/lib/api';
import { cookieSessionTransport } from '@/lib/session-transport';
import { useAuthStore } from '@/stores/auth';
import { parkClinicWork } from '@/stores/workDrafts';
import { finishSessionRecovery, sessionRealm, useSessionRecovery, type SessionOwner } from '@/stores/sessionRecovery';

const same = (a: SessionOwner, b: SessionOwner) => a.id === b.id && a.tenantId === b.tenantId;
let running: { generation: number; promise: Promise<void> } | null = null;

async function recover(
  mode: 'probe' | 'restore' | 'follow',
  queryClient: QueryClient,
  chosenTenantId?: string,
  onFollow?: (user: User) => void,
) {
  const state = useSessionRecovery.getState();
  if (!state.owner || state.phase === 'idle') return;
  if (running?.generation === state.generation) return running.promise;
  const owner = state.owner;
  const transport = cookieSessionTransport();
  useSessionRecovery.setState({ phase: mode === 'probe' ? 'probing' : mode === 'restore' ? 'restoring' : 'following' });
  const operation = (async () => {
    try {
      // A late refresh failure is fenced by generation in the API interceptor.
      await waitForSessionRefresh().catch(() => undefined);
      transport.assertCurrent();
      let user = (await transport.profile()).data;
      transport.assertCurrent();
      if (user.id !== owner.id || sessionRealm(user) !== state.realm || (state.realm !== 'normal' && !same(user, owner))) {
        useSessionRecovery.setState({ phase: 'login-required', observedTenantId: null });
        return;
      }
      if (mode === 'follow' && user.tenantId !== chosenTenantId) {
        useSessionRecovery.setState({ phase: 'choice', observedTenantId: user.tenantId });
        return;
      }
      if (mode === 'probe' && !same(user, owner)) {
        useSessionRecovery.setState({ phase: 'choice', observedTenantId: user.tenantId });
        return;
      }
      if (mode === 'restore' && !same(user, owner)) {
        try { await transport.restore(user, owner.tenantId); }
        catch (error) {
          transport.assertCurrent();
          if ((error as { response?: { status?: number } }).response?.status === 403) {
            useSessionRecovery.setState({ restoreDenied: true });
          }
          throw error;
        }
        transport.assertCurrent();
        user = (await transport.profile()).data;
        transport.assertCurrent();
        if (user.id !== owner.id || sessionRealm(user) !== state.realm) {
          useSessionRecovery.setState({ phase: 'login-required', observedTenantId: null });
          return;
        }
        if (!same(user, owner)) {
          useSessionRecovery.setState({ phase: 'choice', observedTenantId: user.tenantId });
          return;
        }
      }
      // Unlike optional realtime setup during login, a conflicting token MUST abort.
      const socket = await transport.socket(user);
      transport.assertCurrent();
      const finalProfile = (await transport.profile()).data;
      transport.assertCurrent();
      if (!same(finalProfile, user) || sessionRealm(finalProfile) !== state.realm) {
        const canChoose = finalProfile.id === owner.id && state.realm === 'normal' && sessionRealm(finalProfile) === 'normal';
        useSessionRecovery.setState({ phase: canChoose ? 'choice' : 'login-required', observedTenantId: canChoose ? finalProfile.tenantId : null });
        return;
      }
      user = finalProfile;
      if (mode === 'follow' && !same(user, owner)) {
        await queryClient.cancelQueries();
        transport.assertCurrent();
        parkClinicWork(owner);
        queryClient.clear();
      }
      // setAuth with the SAME owner preserves drafts/inbox and never changes the shell key.
      useAuthStore.getState().setAuth(user, socket.data.token);
      finishSessionRecovery();
      if (mode === 'follow' && !same(user, owner)) onFollow?.(user);
    } catch (error) {
      if (isCancel(error) || useSessionRecovery.getState().generation !== state.generation) return;
      const status = (error as { response?: { status?: number } }).response?.status;
      useSessionRecovery.setState({
        phase: status === 401 ? 'login-required' : mode !== 'probe' && (status === 409 || status === 403) ? 'probing' : 'offline',
        observedTenantId: null,
      });
    }
  })();
  running = { generation: state.generation, promise: operation };
  try { await operation; }
  finally {
    if (running?.promise === operation) running = null;
    // Re-probe only after releasing the coalescing slot; a React effect alone
    // could join the just-finishing operation and leave the dialog stuck.
    if (mode !== 'probe' && useSessionRecovery.getState().generation === state.generation &&
        useSessionRecovery.getState().phase === 'probing') await recover('probe', queryClient);
  }
}

export const probeSessionRecovery = (queryClient: QueryClient) => recover('probe', queryClient);
export const restoreLocalSession = (queryClient: QueryClient) => {
  const state = useSessionRecovery.getState();
  if (state.phase !== 'choice' || state.restoreDenied) return Promise.resolve();
  return recover('restore', queryClient);
};

/** Called only by the final, warned confirmation, with its explicitly chosen tenant. */
export const followCookieSession = (queryClient: QueryClient, tenantId: string, onFollow: (user: User) => void) => {
  if (useSessionRecovery.getState().phase !== 'choice') return Promise.resolve();
  return recover('follow', queryClient, tenantId, onFollow);
};

/** Local exit only: never revoke the other tab's valid server session. */
export function leaveRecoveryForLogin(queryClient: QueryClient) {
  if (useSessionRecovery.getState().phase !== 'login-required') return false;
  finishSessionRecovery();
  useAuthStore.getState().expireSession();
  queryClient.clear();
  return true;
}
